import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useEffect, useRef, useState, type ReactNode } from 'react'

import {
  mergeReducedMotionPreset,
  resolveExpandTransition,
  useThemeMotion,
} from '../../animation/themeMotion'
import { AgentStatusDotSlot } from './AgentStatusDot'
import { useTypewriter } from './useTypewriter'

export type AgentProcessLine = {
  id: string
  kind: 'thought' | 'tool' | 'observation' | 'plan' | 'mode' | 'read' | 'note' | 'subagent' | 'question'
  label: string
  detail?: string
  status?: string
  /** 右侧轻量元信息，如「并行 ×2」 */
  meta?: string
}

type AgentProcessFoldProps = {
  summary: string
  status?: string
  /** 运行计时起点（毫秒）：running 时标题旁实时显示「用时 n 秒」，收尾时由 mapper 烙进 summary */
  startedAt?: number
  collapsedByDefault?: boolean
  forceOpen?: boolean
  lines: AgentProcessLine[]
  footer?: ReactNode
  /** 外部持久化展开状态（页面恢复时使用） */
  externalOpen?: boolean
  /** 用户手动展开/收起回调（用于持久化） */
  onToggle?: (open: boolean) => void
}

/** 运行中块的实时用时：每秒跳动；收尾后由 mapper 把最终用时烙进 summary，此组件随之卸载 */
function LiveElapsed({ startedAt }: { startedAt: number }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])
  const seconds = Math.max(1, Math.round((now - startedAt) / 1000))
  return (
    <span className="shrink-0 font-mono text-[11px] text-ink-soft/70" aria-label={`用时 ${seconds} 秒`}>
      （用时 {seconds} 秒）
    </span>
  )
}

function kindPrefix(kind: AgentProcessLine['kind']) {
  switch (kind) {
    case 'thought':
      // 思考行的轮次信息由折叠块标题（第 n 轮思考…）承载，行内不再重复
      return ''
    case 'tool':
      return '工具'
    case 'observation':
      return '观察'
    case 'plan':
      return '计划'
    case 'mode':
      return '模式'
    case 'read':
      return '读取'
    case 'question':
      // 提问行：编号与加粗已在行内样式体现，不加前缀
      return ''
    case 'subagent':
      return '子任务'
    default:
      return ''
  }
}

export function AgentProcessFold({
  summary,
  status,
  startedAt,
  collapsedByDefault = true,
  forceOpen = false,
  lines,
  footer,
  externalOpen,
  onToggle,
}: AgentProcessFoldProps) {
  /** 外部持久化展开态优先（跨页面恢复），否则用 forceOpen / collapsedByDefault 决定 */
  const initialOpen = externalOpen !== undefined ? externalOpen : (forceOpen || !collapsedByDefault)
  const [open, setOpen] = useState(initialOpen)
  const motionPreset = useThemeMotion()
  const reducedMotion = useReducedMotion()
  const expandTrans = resolveExpandTransition(
    mergeReducedMotionPreset(motionPreset, !!reducedMotion),
    { reducedMotion: !!reducedMotion },
  )

  // externalOpen 变化时同步（页面恢复后重新挂载时）
  useEffect(() => {
    if (externalOpen !== undefined) setOpen(externalOpen)
  }, [externalOpen])

  useEffect(() => {
    if (forceOpen) setOpen(true)
  }, [forceOpen])

  // 运行中的折叠块（如「正在思考…」）进入终态后自动收起，用户可再点开回看
  const prevStatusRef = useRef(status)
  useEffect(() => {
    const prev = prevStatusRef.current
    prevStatusRef.current = status
    if (prev === 'running' && (status === 'done' || status === 'completed' || status === 'succeeded')) {
      setOpen(false)
    }
  }, [status])

  const linesBody = <ProcessLines lines={lines} />

  return (
    <div className="agent-process-fold min-w-0">
      <button
        type="button"
        className="group flex w-full min-w-0 items-center gap-2 py-0.5 text-left text-[12px] leading-5 text-ink-soft transition hover:text-ink"
        onClick={() => {
          const next = !open
          setOpen(next)
          onToggle?.(next)
        }}
        aria-expanded={open}
      >
        <AgentStatusDotSlot status={status} size="sm" />
        <span className="min-w-0 flex-1 truncate">{summary}</span>
        {status === 'running' && startedAt != null ? <LiveElapsed startedAt={startedAt} /> : null}
        <motion.svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          className="shrink-0 opacity-70"
          animate={{ rotate: open ? 180 : 0 }}
          transition={expandTrans}
          aria-hidden
        >
          <path d="m6 9 6 6 6-6" />
        </motion.svg>
      </button>

      {reducedMotion ? (
        open ? (
          <div className="pl-3.5">
            {linesBody}
            {footer ? <div className="pt-1.5">{footer}</div> : null}
          </div>
        ) : null
      ) : (
        <>
          <AnimatePresence initial={false}>
            {open ? (
              <motion.div
                key="process-lines"
                initial={{ height: 0, opacity: 0, y: -2 }}
                animate={{ height: 'auto', opacity: 1, y: 0 }}
                exit={{ height: 0, opacity: 0, y: -2 }}
                transition={expandTrans}
                className="overflow-hidden pl-3.5"
              >
                {linesBody}
              </motion.div>
            ) : null}
          </AnimatePresence>
          {/* 确认条放在 overflow 外，避免 border 被裁切 */}
          <AnimatePresence initial={false}>
            {open && footer ? (
              <motion.div
                key="process-footer"
                initial={{ opacity: 0, y: -2 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -2 }}
                transition={expandTrans}
                className="pl-3.5 pt-1.5"
              >
                {footer}
              </motion.div>
            ) : null}
          </AnimatePresence>
        </>
      )}
    </div>
  )
}

function ProcessLines({ lines }: { lines: AgentProcessLine[] }) {
  return (
    <ul className="space-y-0.5 border-l border-[color-mix(in_srgb,var(--line)_36%,transparent)] py-1 pl-2.5">
      {lines.map((line) => {
        // 无 label、无 detail 且非运行中的行没有任何可读内容，直接跳过（否则只剩状态点的空行）
        if (!line.label && !line.detail && line.status !== 'running') return null
        // label 缺省时才用 kind 前缀兜底，避免出现「推理 推理」式重复
        const prefix = line.label ? '' : kindPrefix(line.kind)
        // 思维链行不再渲染行内状态点：折叠块标题已有状态点，行内重复且视觉上像「正文首行多了个点」
        const isThought = line.kind === 'thought'
        // label 为空的行不渲染行头（状态点+空 span 会漏出一个孤点），只渲染 detail
        const hasHeader = Boolean(line.label)
        return (
          <li key={line.id} className="min-w-0">
            {hasHeader ? (
              <div className="flex min-w-0 items-start gap-1.5 text-[12px] leading-5 text-ink-soft">
                {!isThought ? <AgentStatusDotSlot status={line.status} size="sm" /> : null}
                <span className="min-w-0 flex-1">
                  {prefix ? <span className="mr-1 text-ink-soft/80">{prefix}</span> : null}
                  <span className={line.kind === 'question' ? 'text-[13px] font-semibold text-ink' : 'text-ink-soft'}>
                    {line.label}
                  </span>
                  {line.meta ? (
                    <span className="ml-1.5 text-[11px] text-[color-mix(in_srgb,var(--color-success)_70%,var(--ink-soft))]">
                      {line.meta}
                    </span>
                  ) : null}
                </span>
              </div>
            ) : null}
            {line.detail ? (
              <LineDetail
                detail={line.detail}
                running={line.status === 'running'}
                thought={isThought}
              />
            ) : line.status === 'running' ? (
              // 思考首包可能有数秒延迟：运行中且暂无内容时显示省略号占位，避免空块看起来像坏了
              <p className="mt-1 pl-0.5 text-[12px] leading-5 text-ink-soft/60">…</p>
            ) : null}
          </li>
        )
      })}
    </ul>
  )
}

/**
 * 过程明细。思维链（thought）走「引用面板」样式：浅底圆角 + 更浅文字 + 缩小字号，
 * 与正文形成明确的视觉降级（弱化、后退），而不是用加粗/放大去抢正文的层级。
 * 思维链不设内层滚动：滚轮落在思考块上时直接滚动页面——内层滚动容器
 * （scroll-region 的 overscroll-behavior: contain）会吞掉滚轮且到边后页面不动。
 */
function LineDetail({
  detail,
  running,
  thought = false,
}: {
  detail: string
  running: boolean
  thought?: boolean
}) {
  const shown = useTypewriter(detail, running)
  return (
    <p
      className={[
        'mt-1 whitespace-pre-wrap',
        thought
          ? 'rounded-lg bg-[color-mix(in_srgb,var(--surface-muted)_58%,transparent)] px-3 py-2 text-[12px] leading-5 text-[color-mix(in_srgb,var(--ink-soft)_78%,transparent)]'
          : 'scroll-region max-h-44 overflow-auto pl-0.5 text-[12px] leading-5 text-[color-mix(in_srgb,var(--ink-soft)_92%,transparent)]',
      ].join(' ')}
    >
      {shown}
    </p>
  )
}
